import { showChangeModal } from "../components/ChangeModal";

import { ToolType } from "./types";

import { MAX_PAGE_OUTCOMES } from "components/constants";
import { t } from "i18n/i18n";
import { OutcomeChip } from "mds/components/OutcomeChip";
import { docCache } from "sharedb/ShareDBDoc";
import { store, storeApi } from "store/index";
import {
  selectOutcomes,
  selectPageOutcomes,
  selectPages,
  selectSectionOutcomes,
} from "store/selectors";
import { BROADCAST_TOPICS, sendBroadcast } from "utils/broadcast";

/**
 * Takes outcome titles and ensures they are tagged on the current Page, removing any that are not given.
 */
export const modifyPageTaggedOutcomes = async (params: { outcomes: string[] }) => {
  const state = store.getState();
  const doc = docCache.current;
  if (!doc) {
    throw new Error("Worksheet for current Page not found");
  }
  const pageId = selectPages(state).find((p) => p.worksheet_id === doc?.id)?.id;
  if (!pageId) {
    throw new Error("Current Page not found");
  }

  // Limiting outcomes, if more were attempted to tag, we'll just return the max
  const outcomeTitles = params.outcomes.slice(0, MAX_PAGE_OUTCOMES);

  if (outcomeTitles.length === 0) {
    throw new Error("Failed to tag outcomes.");
  }

  const outcomes = selectOutcomes(state);
  const sectionOutcomes = selectSectionOutcomes(state);
  const pageOutcomes = selectPageOutcomes(state);
  const existingPageOutcomes = pageOutcomes.filter((po) => po.page_id === pageId);

  const wantedSectionOutcomes = outcomeTitles
    .map((outcomeTitle) => {
      const outcome = outcomes.find((o) => o.title === outcomeTitle.replace(/^#/, ""));
      const sectionOutcome = sectionOutcomes.find((co) => co.outcome_id === outcome?.id);
      return sectionOutcome;
    })
    .filter((co) => co !== undefined);

  const accepted = await showChangeModal(
    <div>
      {existingPageOutcomes.map((po) => (
        <OutcomeChip
          key={po.id}
          title={
            outcomes.find(
              (o) =>
                o.id === sectionOutcomes.find((co) => co.id === po.section_outcome_id)?.outcome_id,
            )?.title
          }
        />
      ))}
    </div>,
    <div>
      {wantedSectionOutcomes.map((co) => (
        <OutcomeChip key={co.id} title={outcomes.find((o) => o.id === co.outcome_id)?.title} />
      ))}
    </div>,
  );

  if (!accepted) {
    return t("ai.modification_rejected");
  }

  const outcomesToAdd = wantedSectionOutcomes.filter(
    (co) => !existingPageOutcomes.some((po) => po.section_outcome_id === co.id),
  );
  const outcomesToRemove = existingPageOutcomes.filter(
    (po) => !wantedSectionOutcomes.some((co) => co.id === po.section_outcome_id),
  );

  await Promise.all(outcomesToRemove.map((po) => storeApi.page_outcomes.destroy(po.id)));

  await Promise.all(
    outcomesToAdd.map((co) =>
      storeApi.page_outcomes.create({
        page_id: pageId,
        section_id: co.section_id,
        section_outcome_id: co.id,
      }),
    ),
  );

  // This allows for animation on the Page to show that outcomes have been tagged
  sendBroadcast(BROADCAST_TOPICS.AI_OUTCOMES_TAGGED, { pageId });

  return t("ai.modification_accepted");
};

export const modifyPageTaggedOutcomesTool: ToolType<{
  outcomes: string[];
}> = {
  name: "modify_page_tagged_outcomes",
  description: `Tag Outcomes on the current Page. All Pages with interactive content should have at least one outcome tagged.`,
  progressMessage: "Tagging outcomes on the current Page",
  parameters: {
    type: "object",
    properties: {
      outcomes: {
        type: "array",
        minItems: 1,
        maxItems: MAX_PAGE_OUTCOMES,
        items: {
          type: "string",
          description: "The title of the outcome to tag on the Page.",
        },
        description:
          "A list of outcome titles to tag on the Page. This will replace the existing tagged outcomes. To keep existing outcomes, simply include them in the list.",
      },
    },
    required: ["outcomes"],
    additionalProperties: false,
  },
  callback: modifyPageTaggedOutcomes,
};
